import React from 'react';
import { Button, ModalIsland } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../../utils/localization';

import invertedMessages from './messages';

// LaunchConfirmModal — shown before Preview/Launch when blockers are detected.
//
// Two modes driven by `isWarning`:
//   FAIL (isWarning=false): hard block — shows blocker count + item list,
//     only a "Close" button. User must resolve blockers before proceeding.
//   WARNING (isWarning=true): soft block — shows warning message + item list,
//     "Continue" (primary) + "Close" (secondary). User can proceed after
//     acknowledging. onProceed opens the next preview/launch confirm modal.
//
// blockerDetails[].description contains the raw message text sent by the
// backend (b.message || b.description) — not translated via i18n.
function LaunchConfirmModal({
  isOpen,
  blockerCount,
  blockerDetails,
  isWarning,
  onClose,
  onProceed,
}) {
  const l = useInvertedLocalize(invertedMessages);

  const alertTitle = l('Pre-Launch Analysis');
  const itemList = blockerDetails.map((b) => `• ${b.description}`).join('\n');

  const alertSubtitle = isWarning
    ? `${l(
        'Some guardrail checks have warnings. You can still launch, but review the warnings first.'
      )}\n${itemList}`
    : `${l('Launch blocked:')} ${blockerCount} ${l(
        'blocker(s) must be resolved before launching.'
      )}\n${itemList}`;

  return (
    <ModalIsland
      kind='warning'
      isOpen={isOpen}
      onClose={onClose}
      alertTitle={alertTitle}
      alertSubtitle={alertSubtitle}
      isCloseButtonVisible
      primaryButton={
        isWarning ? (
          <Button type='button' kind='filled' size='medium' onClick={onProceed}>
            {l('Continue')}
          </Button>
        ) : (
          <Button type='button' kind='filled' size='medium' onClick={onClose}>
            {l('Close')}
          </Button>
        )
      }
      secondaryButton={
        isWarning ? (
          <Button type='button' kind='ghost' size='medium' onClick={onClose}>
            {l('Close')}
          </Button>
        ) : null
      }
    />
  );
}

LaunchConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  blockerCount: PropTypes.number.isRequired,
  blockerDetails: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  isWarning: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func,
};

LaunchConfirmModal.defaultProps = {
  blockerDetails: [],
  isWarning: false,
  onProceed: null,
};

export default LaunchConfirmModal;
